import { useEffect, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";
import { useStoreState, Session } from "ezStore";

import { Card, FormGroup, Table, Textarea } from "@cofano/cck";

import { Alert, Box, Skeleton, Tooltip } from "@mui/material";
import HistoryToggleOff from "@mui/icons-material/HistoryToggleOff";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import Save from "@mui/icons-material/Save";
import ViewTimeline from "@mui/icons-material/ViewTimeline";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";

import DatePicker from "components/cck/DatePicker";
import DocumentDrawer from "components/documents/DocumentDrawer";

import { FormattedDate } from "utils/DateUtils";
import { DataFormat } from "utils";

import { GetVoyageTimesheetStatusesDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { UpdateTimesheetDocument, UpdateTimesheetNoteDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";
import { VoyageTimesheetCargoActionFragment, VoyageTimesheetStatusFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";
import { CallType } from "__generated__/enums";

type StatusChange = VoyageTimesheetCargoActionFragment["cargo"]["statusChanges"][number];
type Status = VoyageTimesheetStatusFragment;

interface VoyageCargoActionTimesheetProps {
	cargoAction: VoyageTimesheetCargoActionFragment;
	showPdfDrawer?: boolean;
	showOnlyLaytime?: boolean;
	asOverrideSkipperTimes: boolean;
	hideEmptyTimestamps?: boolean;
	refetch: () => void;
	collapsable?: boolean;
	forceReadOnly?: boolean;
}

type TimesheetForm = {
	[statusId: string]: number | null;
};

const VoyageCargoActionTimesheet = ({
	cargoAction,
	showPdfDrawer = true,
	hideEmptyTimestamps,
	showOnlyLaytime,
	asOverrideSkipperTimes,
	refetch,
	collapsable,
	forceReadOnly,
}: VoyageCargoActionTimesheetProps) => {
	const [ editMode, setEditMode ] = useState(false);
	const [ pdfDrawerOpen, setPdfDrawerOpen ] = useState(false);

	const session = useStoreState(store => store.session);

	const [ updateTimesheetNote ] = useMutation(UpdateTimesheetNoteDocument);

	const statusesQuery = useQuery(GetVoyageTimesheetStatusesDocument, {
		variables: {
			search: {
				cargoActionTypes: [ cargoAction.type ],
				laytimeStatus: showOnlyLaytime,
			},
		},
		skip: hideEmptyTimestamps,
	});

	// Get all statuses from the query
	const allStatuses = hideEmptyTimestamps ? cargoAction.cargo.statusChanges.map(statusChange => statusChange.status) : (statusesQuery.data?.allStatuses ?? []);

	const statusChanges = cargoAction.cargo.statusChanges
		.filter(s => s.status.cargoActionType === cargoAction.type)
		.filter(s => showOnlyLaytime ? (s.status.laytimeStartStatus || s.status.laytimeEndStatus) == showOnlyLaytime : true);

	const statusesWithChanges = allStatuses.map((status) => {
		return {
			...status,
			statusChange: statusChanges.findLast((statusChange) => statusChange.status.id === status.id),
		};
	});

	const getDefaultValues = () => {
		return statusesWithChanges.reduce((statusAcc, status) => {
			if (asOverrideSkipperTimes) {
				statusAcc[status.id.toString()] = status.statusChange?.actualTime ? new Date(status.statusChange.actualTime).getTime() : null;
			} else {
				statusAcc[status.id.toString()] = status.statusChange?.actualTimeOriginal ? new Date(status.statusChange.actualTimeOriginal).getTime() : null;
			}

			return statusAcc;
		}, {} as TimesheetForm);
	};

	const { control, handleSubmit, reset } = useForm<TimesheetForm>({
		defaultValues: getDefaultValues(),
	});

	const [ timesheetNote, setTimesheetNote ] = useState<string>(cargoAction.timesheetNote ?? "");

	useEffect(() => {
		setTimesheetNote(cargoAction.timesheetNote ?? "");
	}, [ cargoAction.id ]);

	const [ updateTimesheet, { loading: mutationLoading } ] = useMutation(UpdateTimesheetDocument);

	const onSubmit = async (data: TimesheetForm) => {
		try {
			const getStatus = (statusId: number) => {
				return statusesWithChanges.find(status => status.id === statusId);
			};

			const entries = Object.entries(data)
				.map(([ statusId, actualTime ]) => ({
					statusId: parseInt(statusId),
					actualTime: actualTime,
				}))
				.filter(entry => {
					const existingStatusChange = getStatus(entry.statusId)?.statusChange;
					return entry.actualTime !== null || existingStatusChange;
				});

			const executeMutations = async () => {
				await updateTimesheet({
					variables: {
						cargoId: cargoAction.cargo.id,
						entries: entries,
						asOverrideSkipperTimes: asOverrideSkipperTimes,
					},
				});

				await updateTimesheetNote({
					variables: {
						actionId: cargoAction.id,
						note: timesheetNote,
					},
				});
			};

			await executeMutations();
			setEditMode(false);
			refetch();
		} catch (error) {
			console.error("Failed to update timesheet:", error);
		}
	};

	const body = statusesQuery.loading ? (
		<Loading />
	) : editMode ? (
		<Edit
			cargoAction={cargoAction}
			allStatuses={allStatuses}
			control={control}
			session={session}
		/>
	) : (
		<Details
			cargo={cargoAction.cargo}
			statusesWithChanges={statusesWithChanges}
			session={session}
			showOverrides={asOverrideSkipperTimes}
		/>
	);

	return (
		<Card
			collapsable={collapsable}
			title={intl.formatMessage({ id: cargoAction.type }) + " " + intl.formatMessage({ id: "TIMESHEET" })}
			icon={<ViewTimeline fontSize="small" color="primary" />}
			actions={[
				{
					icon: <InsertDriveFile fontSize="small" />,
					onClick: () => {
						setPdfDrawerOpen(true);
					},
					tooltip: intl.formatMessage({ id: "EXPORT" }),
					hidden: editMode || !showPdfDrawer,
				},
				{
					icon: <Save fontSize="small" />,
					onClick: handleSubmit(onSubmit),
					tooltip: intl.formatMessage({ id: "SAVE" }),
					hidden: !editMode,
					disabled: mutationLoading,
				},
				{
					icon: <EditIcon fontSize="small" />,
					tooltip: intl.formatMessage({ id: "EDIT" }),
					onClick: () => {
						reset(getDefaultValues());
						setEditMode(true);
					},
					hidden: editMode || forceReadOnly,
				},
				{
					icon: <BlockIcon fontSize="small" />,
					tooltip: intl.formatMessage({ id: "CANCEL" }),
					onClick: () => {
						reset(getDefaultValues());
						setEditMode(false);
					},
					hidden: !editMode,
					disabled: mutationLoading,
				},

			]}
		>
			{body}

			<div style={{ marginTop: 16 }}>
				<FormGroup
					label={`${intl.formatMessage({ id: "TIMESHEET_NOTE" })}`}
					labelPlacement="top"
					labelWidth={160}
					overflow="hidden"
				>
					{editMode ? (
						<Textarea
							value={timesheetNote}
							onChange={(e) => setTimesheetNote(e.target.value)}
						/>
					) : (
						<div style={{ fontSize: "small", whiteSpace: "pre-wrap" }}>
							{cargoAction.timesheetNote ?? "-"}
						</div>
					)}
				</FormGroup>
			</div>

			{showPdfDrawer && (
				<DocumentDrawer
					url={`/cargos/pdf/${cargoAction.cargo.id}/timesheet`}
					queryParams={{ callTypes: cargoAction.type }}
					open={pdfDrawerOpen}
					multilingual
					onClose={() => setPdfDrawerOpen(false)}
				/>
			)}
		</Card>
	);
};

const Loading = () => {
	return Array.from(Array(15)).map((index) => (
		<Skeleton variant="text" height={30} key={index} />
	));
};

const Details = ({ cargo, statusesWithChanges, session, showOverrides }: {
	cargo: VoyageTimesheetCargoActionFragment["cargo"];
	statusesWithChanges: (Status & { statusChange?: StatusChange })[];
	session: Session;
	showOverrides: boolean;
}) => {

	const isStatusActive = (status: Status) => {
		return cargo.lastStatusChange?.status.id === status.id;
	};

	const isStatusApplied  = (status: Status) => {
		return cargo.statusChanges.filter(x => x.status.id == status.id).length > 0;
	};

	// Create columns: Status column + one column per cargo
	const columns = [
		{
			id: "status",
			title: intl.formatMessage({ id: "STATUS" }),
			render: (status: Status) =>
				<span style={{
					fontWeight: isStatusActive(status) ? "bold" : "normal",
					fontStyle: isStatusApplied(status) ? "normal" : "italic",
					opacity: isStatusApplied(status) ? 1 : 0.8,
				}}>
					{DataFormat.status(status, session.locale)}
				</span>,
		},
		{
			id: `cargo-${cargo.id}`,
			title: `${intl.formatMessage({ id: "CARGO" })} ${cargo.cargoNumber ?? ""}`,
			render: (status: Status) => {
				const cargoStatus = statusesWithChanges.find(s => s.id === status.id);
				const isApplied = cargo.statusChanges.filter(x => x.status.id === status.id).length > 0;
				const isActive = cargo.lastStatusChange?.status.id === status.id;
				const time = showOverrides ? cargoStatus?.statusChange?.actualTime : cargoStatus?.statusChange?.actualTimeOriginal;
				const isOverride = showOverrides && cargoStatus?.statusChange?.actualTimeOriginal &&
					cargoStatus.statusChange.actualTimeOverride &&
					cargoStatus.statusChange.actualTimeOriginal != cargoStatus.statusChange.actualTimeOverride;

				return (
					<span
						style={{
							alignItems: "center",
							display: "flex",
							gap: "6px",
							fontStyle: !cargoStatus?.statusChange ? "italic" : undefined,
							fontWeight: isActive ? "bold" : undefined,

						}}>
						{time ? (
							<FormattedDate date={time} format="lll" />
						) : (
							isApplied ? "-" : undefined
						)}
						{isOverride && (
							<Tooltip title={<>{intl.formatMessage({ id: "CREW_TIMESTAMP" })}: <FormattedDate date={cargoStatus.statusChange?.actualTimeOriginal} /></>}>
								<HistoryToggleOff fontSize="small" htmlColor="gray" />
							</Tooltip>
						)}
					</span>
				);
			},
		},
	];

	return (
		<Table
			id="timesheet"
			key={`${cargo.id}-${cargo.statusChanges.length}-${statusesWithChanges.map(s => showOverrides ? s.statusChange?.actualTime : s.statusChange?.actualTimeOriginal).join(",")}`}
			disableFooter
			columns={columns}
			content={statusesWithChanges}
		/>
	);
};

const Edit = ({ cargoAction, allStatuses, control, session }: {
	cargoAction: VoyageTimesheetCargoActionFragment;
	allStatuses: Status[];
	control: Control<TimesheetForm>;
	session: Session;
}) => {

	const isDischargingTimesheet = cargoAction.type === CallType.Discharging;

	const loadingStillActive = !!cargoAction.cargo.lastStatusChange?.status.cargoActionType
		&& cargoAction.cargo.lastStatusChange?.status.cargoActionType === CallType.Loading;

	const loadingTimesheetEmpty = cargoAction.cargo.statusChanges
		.filter(sc => sc.status.cargoActionType === CallType.Loading)
		.every(sc => !sc.actualTimeOriginal && !sc.actualTime);

	const showTimesheetWarning = isDischargingTimesheet && (loadingStillActive || loadingTimesheetEmpty);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: "16px",
				width: "100%",
				overflow: "visible",
			}}>
			{showTimesheetWarning && (
				<Alert severity="warning">{intl.formatMessage({ id: "TIMESHEET_LOADING_NOT_COMPLETED_WARNING" })}</Alert>
			)}
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					gap: "8px",
				}}>
				<Box
					sx={{
						flexShrink: 0,
						fontWeight: 700,
						width: "200px",
					}}>
					{intl.formatMessage({ id: "STATUS" })}
				</Box>

				<Box key={cargoAction.cargo.id} sx={{ fontWeight: 700 }}>
					{intl.formatMessage({ id: "CARGO" })} {cargoAction.cargo.cargoNumber ?? ""}
				</Box>
			</Box>

			{allStatuses.map((status) => (
				<Box
					key={status.id}
					sx={{
						alignItems: "flexStart",
						display: "flex",
						gap: "8px",
					}}>
					<Box
						sx={{
							flexShrink: 0,
							fontWeight: 500,
							paddingTop: "8px",
							width: "200px",
						}}>
						{DataFormat.status(status, session.locale)}
					</Box>

					<Box sx={{ width: "100%" }}>
						<Box>
							<Controller
								name={status.id.toString()}
								control={control}
								render={({ field: { onChange, value } }) => (
									<DatePicker
										onChange={onChange}
										value={value ?? undefined}
										showTimeInput
										showDateSelect
										timeIntervals={1}
										placeholderText=" "
									/>
								)}
							/>
						</Box>
					</Box>
				</Box>
			))}
		</Box>
	);
};

export default VoyageCargoActionTimesheet;
